import { getSocket } from '@/hooks/useRealtimeSync';

export interface PresenceState {
  online: boolean;
  lastSeenAt: string | null;
}

const ONLINE_THRESHOLD_MS = 5 * 60 * 1000; // 5 минут
const ACTIVITY_HINT_ONLINE_MS = 15 * 1000;

function isRecentlyOnline(lastSeenAt: string | null): boolean {
  if (!lastSeenAt) return false;
  return Date.now() - new Date(lastSeenAt).getTime() < ONLINE_THRESHOLD_MS;
}

const presenceMap = new Map<string, PresenceState>();
const listeners = new Map<string, Set<() => void>>();
const activityHintTimers = new Map<string, ReturnType<typeof setTimeout>>();

const EMPTY_PRESENCE: PresenceState = Object.freeze({ online: false, lastSeenAt: null });

function emit(userId: string): void {
  listeners.get(userId)?.forEach((cb) => cb());
}

export function getPresenceSnapshot(userId: string): PresenceState {
  return presenceMap.get(userId) ?? EMPTY_PRESENCE;
}

export function subscribePresence(userId: string, cb: () => void): () => void {
  let set = listeners.get(userId);
  if (!set) {
    set = new Set();
    listeners.set(userId, set);
  }
  set.add(cb);
  return () => {
    set!.delete(cb);
    if (set!.size === 0) listeners.delete(userId);
  };
}

export function seedPresenceFromUsers(
  users: { id: string; last_seen_at?: string | null }[]
): void {
  for (const u of users) {
    if (!u.id) continue;
    const existing = presenceMap.get(u.id);
    const lastSeenAt = u.last_seen_at ?? null;
    const online = existing?.online ?? false;
    const next: PresenceState = { online, lastSeenAt };
    presenceMap.set(u.id, next);
    emit(u.id);
  }
}

export function applyPresenceEvent(data: { userId?: string; online?: boolean; lastSeenAt?: string }): void {
  const userId = data?.userId;
  if (!userId) return;
  const hintTimer = activityHintTimers.get(userId);
  if (hintTimer) {
    clearTimeout(hintTimer);
    activityHintTimers.delete(userId);
  }
  const prev = presenceMap.get(userId);
  const lastSeenAt = data.lastSeenAt ?? prev?.lastSeenAt ?? null;
  const online = data.online ?? isRecentlyOnline(lastSeenAt);
  const next: PresenceState = { online, lastSeenAt };
  presenceMap.set(userId, next);
  emit(userId);
}

export function markUserRecentlyActive(userId: string | null | undefined, at = new Date().toISOString()): void {
  if (!userId) return;
  const prev = presenceMap.get(userId);
  presenceMap.set(userId, {
    online: true,
    lastSeenAt: at ?? prev?.lastSeenAt ?? null,
  });
  emit(userId);

  const prevTimer = activityHintTimers.get(userId);
  if (prevTimer) clearTimeout(prevTimer);
  const timer = setTimeout(() => {
    activityHintTimers.delete(userId);
    const cur = presenceMap.get(userId);
    if (!cur || cur.lastSeenAt !== at || !cur.online) return;
    presenceMap.set(userId, { ...cur, online: false });
    emit(userId);
  }, ACTIVITY_HINT_ONLINE_MS);
  activityHintTimers.set(userId, timer);
}

export function resetPresenceStore(): void {
  for (const timer of activityHintTimers.values()) clearTimeout(timer);
  presenceMap.clear();
  listeners.clear();
  activityHintTimers.clear();
}

export function installPresenceSocketBridge(): () => void {
  const socket = getSocket();
  const handler = (data: { userId?: string; online?: boolean; lastSeenAt?: string }) => {
    applyPresenceEvent(data);
  };
  socket.on('user:presence', handler);
  return () => {
    socket.off('user:presence', handler);
  };
}
